import { Inject, Injectable } from '@angular/core';
import { CrudService } from '@services/firestore-crud.service';
import { Firestore } from 'firebase/firestore';
import { FirebaseStorage } from 'firebase/storage';
import { FIRESTORE_TOKEN, STORAGE_TOKEN } from './firebase.tokens';

export interface Instagram {
  id?: string | null;
  title: string | null;
  slug: string | null;
  type?: InstagramPostType | null;
  embeddedCode: string | null;
  likes?: number | null;
  comments?: number | null;
  shares?: number | null;
  saves?: number | null;
  reach?: number | null;
  views?: number | null;
  profileVisits?: number | null;
  follows?: number | null;
  reposts?: number | null;
  nonFollowers?: number | null;
  rating?: number | null;
  created: number | null;
  updated: number | null;
}

export enum InstagramPostType {
  city = 'city',
  nature = 'nature',
  filmography = 'filmography',
  sport = 'sport',
  events = 'events',
  dinasties = 'dinasties',
  chronology = 'chronology',
  art = 'art',
  saints = 'saints',
  countries = 'drugi-narodi',
  opinion = 'stav-autora',
  heroes = 'junaci',
  culture = 'culture',
  books = 'books',
  music = 'music',
  entrepreneurs = 'entrepreneurs',
  generations = 'generations',
}

export enum InstagramPostSlugs {
  city = 'gradovi-i-opstine',
  nature = 'priroda',
  filmography = 'film-i-pozoriste',
  sport = 'sport',
  events = 'istorijski-dogadjaji',
  dinasties = 'dinastije',
  saints = 'pravoslavlje',
  art = 'umetnost',
  countries = 'drugi-narodi',
  opinion = 'stav-autora',
  heroes = 'junaci',
  culture = 'kultura',
  books = 'knjizevnost',
  music = 'muzika',
  entrepreneurs = 'privreda',
  generations = 'generacije',
}

export enum InstagramPostName {
  city = 'Градови и општине',
  nature = 'Природа и здања',
  filmography = 'Филм и позориште',
  sport = 'Спорт',
  events = 'Историјски догађаји',
  dinasties = 'Династије',
  saints = 'Православље',
  art = 'Визуелна уметност',
  countries = 'Други народи',
  opinion = 'Ауторски текст',
  heroes = 'Јунаци, активизам, политика',
  culture = 'Наука и култура',
  books = 'Књижевност',
  music = 'Музика',
  entrepreneurs = 'Привреда',
  generations = 'Генерације',
}

export interface InstagramCategory {
  id: InstagramPostType;
  image: string;
  link: InstagramPostSlugs;
  name: InstagramPostName;
  order: number;
}

export const instragramTypes: InstagramCategory[] = [
  {
    id: InstagramPostType.events,
    image: `url('assets/images/instagram-images/instagram-events.webp')`,
    link: InstagramPostSlugs.events,
    name: InstagramPostName.events,
    order: 1,
  },
  {
    id: InstagramPostType.dinasties,
    image: `url('assets/images/instagram-images/instagram-dinasties.webp')`,
    link: InstagramPostSlugs.dinasties,
    name: InstagramPostName.dinasties,
    order: 2,
  },
  {
    id: InstagramPostType.heroes,
    image: `url('assets/images/instagram-images/instagram-heroes.webp')`,
    link: InstagramPostSlugs.heroes,
    name: InstagramPostName.heroes,
    order: 3,
  },
  {
    id: InstagramPostType.saints,
    image: `url('assets/images/instagram-images/instagram-saints.webp')`,
    link: InstagramPostSlugs.saints,
    name: InstagramPostName.saints,
    order: 4,
  },
  {
    id: InstagramPostType.books,
    image: `url('assets/images/instagram-images/instagram-books.webp')`,
    link: InstagramPostSlugs.books,
    name: InstagramPostName.books,
    order: 5,
  },
  {
    id: InstagramPostType.music,
    image: `url('assets/images/instagram-images/instagram-music.webp')`,
    link: InstagramPostSlugs.music,
    name: InstagramPostName.music,
    order: 6,
  },
  {
    id: InstagramPostType.filmography,
    image: `url('assets/images/instagram-images/instagram-filmography.webp')`,
    link: InstagramPostSlugs.filmography,
    name: InstagramPostName.filmography,
    order: 7,
  },
  {
    id: InstagramPostType.art,
    image: `url('assets/images/instagram-images/instagram-art.webp')`,
    link: InstagramPostSlugs.art,
    name: InstagramPostName.art,
    order: 8,
  },
  {
    id: InstagramPostType.culture,
    image: `url('assets/images/instagram-images/instagram-culture.webp')`,
    link: InstagramPostSlugs.culture,
    name: InstagramPostName.culture,
    order: 9,
  },
  {
    id: InstagramPostType.nature,
    image: `url('assets/images/instagram-images/instagram-nature.webp')`,
    link: InstagramPostSlugs.nature,
    name: InstagramPostName.nature,
    order: 10,
  },
  {
    id: InstagramPostType.entrepreneurs,
    image: `url('assets/images/instagram-images/instagram-entrepreneurs.webp')`,
    link: InstagramPostSlugs.entrepreneurs,
    name: InstagramPostName.entrepreneurs,
    order: 11,
  },
  {
    id: InstagramPostType.sport,
    image: `url('assets/images/instagram-images/instagram-sport.webp')`,
    link: InstagramPostSlugs.sport,
    name: InstagramPostName.sport,
    order: 12,
  },
  {
    id: InstagramPostType.city,
    image: `url('assets/images/instagram-images/instagram-city.webp')`,
    link: InstagramPostSlugs.city,
    name: InstagramPostName.city,
    order: 13,
  },
  {
    id: InstagramPostType.countries,
    image: `url('assets/images/instagram-images/instagram-countries.webp')`,
    link: InstagramPostSlugs.countries,
    name: InstagramPostName.countries,
    order: 14,
  },
  {
    id: InstagramPostType.generations,
    image: `url('assets/images/instagram-images/instagram-generations.webp')`,
    link: InstagramPostSlugs.generations,
    name: InstagramPostName.generations,
    order: 15,
  },
  {
    id: InstagramPostType.opinion,
    image: `url('assets/images/instagram-images/instagram-opinion.webp')`,
    link: InstagramPostSlugs.opinion,
    name: InstagramPostName.opinion,
    order: 16,
  },
];

@Injectable({ providedIn: 'root' })
export class InstagramService extends CrudService<Instagram> {
  override collectionName = 'instagram';

  constructor(
    @Inject(FIRESTORE_TOKEN) firestore: Firestore,
    @Inject(STORAGE_TOKEN) storage: FirebaseStorage
  ) {
    super(firestore, storage);
  }

  calculateRating(post: Instagram) {
    return ((post.views ?? 0) / 10000)
      + ((post.likes ?? 0) / 250)
      + ((post.comments ?? 0) / 75)
      + ((post.saves ?? 0) / 50)
      + ((post.profileVisits ?? 0) / 50)
      + ((post.shares ?? 0) / 40)
      + ((post.reposts ?? 0) / 40)
      + ((post.follows ?? 0) / 20);
  }

}
